import React, { useState } from 'react';
import { Search, Shield, Users, UserCheck, Lock, Eye, UserPlus } from 'lucide-react';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import StatusSwitch from '../common/StatusSwitch';
import Skeleton from '../common/Skeleton';
import { statusBadgeCss } from '../../utils/statusVariant';

type RoleKey = 'ALL' | 'ADMIN' | 'MANAGER' | 'ANALYST' | 'VIEWER';

const ROLE_SECTIONS: { key: RoleKey; label: string; icon: React.ElementType }[] = [
  { key: 'ALL',     label: 'All Users',  icon: Users     },
  { key: 'ADMIN',   label: 'Admin',      icon: Shield    },
  { key: 'MANAGER', label: 'Manager',    icon: UserCheck },
  { key: 'ANALYST', label: 'Analyst',    icon: Lock      },
  { key: 'VIEWER',  label: 'Viewer',     icon: Eye       },
];

type UserStatus = 'Active' | 'Inactive' | 'Pending';

interface UserRow {
  id: number;
  name: string;
  email: string;
  role: Exclude<RoleKey, 'ALL'>;
  department: string;
  status: UserStatus;
  lastLogin: string;
}

const DUMMY_USERS: UserRow[] = [
  { id: 1,  name: 'Srinivas Rao',    email: 'srinivas@sagsol.in',  role: 'ADMIN',   department: 'IT',         status: 'Active',   lastLogin: '2026-05-15' },
  { id: 2,  name: 'Priya Sharma',    email: 'priya@sagsol.in',     role: 'MANAGER', department: 'Compliance', status: 'Active',   lastLogin: '2026-05-14' },
  { id: 3,  name: 'Ravi Kumar',      email: 'ravi@sagsol.in',      role: 'ANALYST', department: 'Risk',       status: 'Active',   lastLogin: '2026-05-13' },
  { id: 4,  name: 'Anita Mehta',     email: 'anita@sagsol.in',     role: 'VIEWER',  department: 'Audit',      status: 'Inactive', lastLogin: '2026-04-30' },
  { id: 5,  name: 'Kiran Reddy',     email: 'kiran@sagsol.in',     role: 'MANAGER', department: 'Finance',    status: 'Active',   lastLogin: '2026-05-12' },
  { id: 6,  name: 'Deepa Nair',      email: 'deepa@sagsol.in',     role: 'ANALYST', department: 'Compliance', status: 'Pending',  lastLogin: '—'          },
  { id: 7,  name: 'Suresh Babu',     email: 'suresh@sagsol.in',    role: 'VIEWER',  department: 'IT',         status: 'Active',   lastLogin: '2026-05-10' },
  { id: 8,  name: 'Lakshmi Devi',    email: 'lakshmi@sagsol.in',   role: 'ANALYST', department: 'Risk',       status: 'Active',   lastLogin: '2026-05-11' },
  { id: 9,  name: 'Manikanta Goud',  email: 'mani@sagsol.in',      role: 'ADMIN',   department: 'IT',         status: 'Active',   lastLogin: '2026-05-15' },
  { id: 10, name: 'Venkat Prasad',   email: 'venkat@sagsol.in',    role: 'VIEWER',  department: 'Audit',      status: 'Inactive', lastLogin: '2026-03-20' },
  { id: 11, name: 'Sowmya Reddy',    email: 'sowmya@sagsol.in',    role: 'MANAGER', department: 'HR',         status: 'Active',   lastLogin: '2026-05-09' },
  { id: 12, name: 'Arun Teja',       email: 'arun@sagsol.in',      role: 'ANALYST', department: 'Finance',    status: 'Pending',  lastLogin: '—'          },
];


const ROLE_PILL: Record<Exclude<RoleKey, 'ALL'>, string> = {
  ADMIN:   'status-badge xs info',
  MANAGER: 'status-badge xs info',
  ANALYST: 'status-badge xs info',
  VIEWER:  'status-badge xs neutral',
};

const UserRolesTab: React.FC = () => {
  const [activeRole, setActiveRole]     = useState<RoleKey>('ALL');
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading] = useState(false);

  const filtered = DUMMY_USERS.filter(u => {
    const matchRole   = activeRole === 'ALL' || u.role === activeRole;
    const matchStatus = statusFilter === 'all' || u.status.toLowerCase() === statusFilter;
    const matchSearch = !searchTerm || u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchRole && matchStatus && matchSearch;
  });

  return (
    <div className="flex h-full overflow-hidden">

      {/* Left nav */}
      <div className="netbody-card side-nav-card">
        <div className="side-nav-card-heading">Roles</div>
        <nav>
          {ROLE_SECTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveRole(key)}
              className={`side-panel-menu-item${activeRole === key ? ' side-panel-menu-item--active' : ''}`}
              data-testid={`NAV_user_roles_${key.toLowerCase()}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Right card */}
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">

        {/* Toolbar */}
        <div className="netbody-header">
          {/* Active/Inactive availability — iOS switch. Shares the single
              statusFilter with the All/Pending dropdown; last touched wins. */}
          <StatusSwitch
            value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
            onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
            noun="users"
            testId="SEL_user_roles_status"
          />
          <div className="flex items-center gap-2 ml-auto">
            <FormControl.SearchSelect
              value={['active', 'inactive'].includes(statusFilter) ? 'all' : statusFilter}
              onChange={v => setStatusFilter(String(v ?? 'all'))}
              className="filter-status"
              data-testid="SEL_user_roles_status_extra"
              noSearchIcon
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
              ]}
            />

            <FormControl.Input
              type="text"
              placeholder="Search users…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_user_roles_search"
            />

            <Button variant="fill" icon={UserPlus} data-testid="BTN_user_roles_invite">
              Invite User
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Name</th>
                      <th className="table-list-th">Email</th>
                      <th className="table-list-th">Role</th>
                      <th className="table-list-th">Department</th>
                      <th className="table-list-th">Last Login</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'md', 'sm', 'sm', 'xs', 'pill']} />}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="table-list-empty-cell">
                          No users found.
                        </td>
                      </tr>
                    )}
                    {filtered.map((u, idx) => (
                      <tr key={u.id} className="table-list-row">
                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{u.name}</span>
                        </td>
                        <td className="table-list-cell">{u.email}</td>
                        <td className="table-list-cell">
                          <span className={ROLE_PILL[u.role]}>{u.role}</span>
                        </td>
                        <td className="table-list-cell">{u.department}</td>
                        <td className="table-list-cell">{u.lastLogin}</td>
                        <td className="table-list-cell">
                          <span className={statusBadgeCss(u.status)}>{u.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserRolesTab;
